import { z } from 'zod';
import { decimalString, paginatedResponse } from './common';

/**
 * Project Organization — tender costing & labor-hour distribution.
 *
 * Tender: for a selected project, percentage terms (profit / commission /
 * additional costs) are applied to the offer total WITHOUT VAT
 * (Project.totalNet — the figure quoted to customers as "X Lei+TVA"), and the
 * remainder after subtracting the BOM material cost funds labor:
 *
 *   laborCost  = totalNet − materialsCost − profit − commission − additional
 *   laborHours = laborCost / mean(five operation hourly rates)
 *
 * Manual tender: the same computation over a hand-entered material cost and
 * its own three percentages, so an estimated scenario can be compared against
 * the BOM-derived one. `manualMaterialsCost` is null until a figure is typed —
 * that null is what suppresses the manual labor cost and the materials
 * difference (`materialsCost − manualMaterialsCost`) instead of reporting a
 * bogus "0 Lei of materials" scenario.
 *
 * Labor cost: global hourly production rates per operation (Circular, Cant,
 * CNC, Vopsitorie, Asamblare, Montaj, Proiectare) plus a manual per-project
 * distribution of the labor-hour budget across those operations.
 *
 * The "Tarif mediu" and the money→hours conversion factor are the operation
 * rates weighted by their time shares — see `tenderAverageHourlyRate`.
 * `tenderLaborRateCodes` is kept for reference to the original
 * manufacturing-only subset but no longer gates the average.
 */

export const tenderOperationCodes = [
  'CIRCULAR',
  'CANT',
  'CNC',
  'VOPSITORIE',
  'ASAMBLARE',
  'MONTAJ',
  'PROIECTARE',
] as const;
export const tenderOperationCodeEnum = z.enum(tenderOperationCodes);
export type TenderOperationCode = z.infer<typeof tenderOperationCodeEnum>;

/**
 * The original manufacturing-only subset of operation rates.
 *
 * Historically this was the divisor behind `tenderAverageHourlyRate`: the
 * "Tarif mediu" averaged only these five so Vopsitorie and Proiectare, though
 * priced, did not move the money→hours factor. That is no longer the case — the
 * average now spans every `tenderOperationCodes` entry by product decision — so
 * this constant is retained only for reference and is not used in the arithmetic.
 */
export const tenderLaborRateCodes = [
  'CIRCULAR',
  'CANT',
  'CNC',
  'ASAMBLARE',
  'MONTAJ',
] as const satisfies readonly TenderOperationCode[];

/** Standard percent choices offered in the Tender combobox; manual values stay allowed. */
export const tenderPercentPresets = [1, 3, 5, 10, 15] as const;

/**
 * Which costing scenario supplies the labour-hour budget the Manufacturing
 * operations distribution hands out. AUTOMATIC unless a manual scenario has
 * been entered and deliberately chosen.
 */
export const tenderHoursSourceEnum = z.enum(['AUTOMATIC', 'MANUAL']);
export type TenderHoursSourceValue = z.infer<typeof tenderHoursSourceEnum>;

export const TENDER_DEFAULT_PROFIT_PCT = 15;
export const TENDER_DEFAULT_COMMISSION_PCT = 5;
export const TENDER_DEFAULT_ADDITIONAL_PCT = 3;

/** Percent of the offer total; two decimals kept server-side. */
const percentValue = z.number().min(0).max(100);
/** Hand-entered material cost; bounded by the Decimal(12,2) column. */
const moneyValue = z.number().min(0).max(9999999999.99);
/** Hourly production rate in RON/hour. */
const hourlyRate = z.number().min(0).max(100000);
/** Reserved hours for one operation; decimals allowed. */
const reservedHoursValue = z.number().min(0).max(100000);

const perOperation = <T extends z.ZodTypeAny>(value: T) =>
  z.object({
    CIRCULAR: value,
    CANT: value,
    CNC: value,
    VOPSITORIE: value,
    ASAMBLARE: value,
    MONTAJ: value,
    PROIECTARE: value,
  });

/** RON/hour per operation. */
export const tenderOperationRates = perOperation(z.number());
export type TenderOperationRates = z.infer<typeof tenderOperationRates>;

/** Reserved hours per operation. */
export const tenderReservedHours = perOperation(z.number());
export type TenderReservedHours = z.infer<typeof tenderReservedHours>;

/**
 * Percentage of a project's labour-hour budget each operation typically takes.
 * Global, like the rates: the split describes how the shop works, not one
 * project. They normally sum to 100, but that is not enforced — see the schema.
 */
export const tenderOperationShares = perOperation(z.number());
export type TenderOperationShares = z.infer<typeof tenderOperationShares>;

export const tenderRatesResponse = z.object({
  rates: tenderOperationRates,
  /** Hourly rates weighted by their time shares (RON/h, 2dp). */
  averageHourlyRate: z.number(),
  shares: tenderOperationShares,
  /** Sum of the five shares, so the page can flag a split that misses 100%. */
  sharesTotal: z.number(),
  updatedAt: z.string().datetime(),
});
export type TenderRatesResponse = z.infer<typeof tenderRatesResponse>;

// .strict() is explicit for the same reason it is on updateProjectTenderRequest
// below: .refine() wraps this in ZodEffects, which the global pipe does not
// strict-upgrade, so a typoed key would be stripped instead of rejected.
export const updateTenderRatesRequest = z
  .object({
    /** Only the rates being changed. */
    rates: perOperation(hourlyRate).partial().strict().optional(),
    /** Only the shares being changed. */
    shares: perOperation(percentValue).partial().strict().optional(),
  })
  .strict()
  .refine(
    (v) =>
      Object.values(v.rates ?? {}).some((value) => value !== undefined) ||
      Object.values(v.shares ?? {}).some((value) => value !== undefined),
    { message: 'At least one rate or share is required.' },
  );
export type UpdateTenderRatesRequest = z.infer<typeof updateTenderRatesRequest>;

export const projectTenderResponse = z.object({
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  currency: z.string(),
  /**
   * Offer total without VAT — the base for every percentage. Normally
   * Project.totalNet, but the price hand-entered on the project takes over
   * when there is one (see totalOfferIsManual).
   */
  totalOfferNet: decimalString,
  /** Offer total with VAT, for display only. */
  totalOfferGross: decimalString,
  /**
   * Net material cost: the BOM grand total across all rooms, or the cost
   * hand-entered on the project when there is one (see materialsCostIsManual).
   */
  materialsCost: decimalString,
  /**
   * True when the figure above came from the project's manual pricing rather
   * than from the items. Reported so the page can say which it is showing —
   * the two are not interchangeable to anyone reading the costing.
   */
  materialsCostIsManual: z.boolean(),
  totalOfferIsManual: z.boolean(),
  profitPct: z.number(),
  commissionPct: z.number(),
  additionalPct: z.number(),
  profitAmount: decimalString,
  commissionAmount: decimalString,
  additionalAmount: decimalString,
  /**
   * totalOfferNet − materialsCost − profitAmount − commissionAmount −
   * additionalAmount; negative when the offer does not cover the costs.
   */
  laborCost: decimalString,
  /** Share-weighted mean of the operation rates at response time (RON/h, 2dp). */
  averageHourlyRate: z.number(),
  /**
   * laborCost / averageHourlyRate (2dp); null while the average rate is 0 or
   * the project currency is not RON (the rates are RON/h, so the division
   * would silently mix currencies).
   */
  laborHours: z.number().nullable(),

  /** Hand-entered material cost; null until a figure is saved. */
  manualMaterialsCost: decimalString.nullable(),
  manualProfitPct: z.number(),
  manualCommissionPct: z.number(),
  manualAdditionalPct: z.number(),
  /** Percentages of totalOfferNet — the same base the automatic row uses. */
  manualProfitAmount: decimalString,
  manualCommissionAmount: decimalString,
  manualAdditionalAmount: decimalString,
  /** As laborCost but over manualMaterialsCost; null while that is null. */
  manualLaborCost: decimalString.nullable(),
  /** As laborHours but over manualLaborCost; null under the same conditions. */
  manualLaborHours: z.number().nullable(),
  /**
   * materialsCost − manualMaterialsCost; null while no manual figure exists.
   * Positive means the manual estimate came in under the BOM total.
   */
  materialsCostDifference: decimalString.nullable(),
  /**
   * Which scenario's hours the operation distribution works from. MANUAL is
   * only honoured while manualLaborHours exists — a chosen scenario that was
   * later cleared falls back to the automatic budget rather than to nothing.
   */
  hoursSource: tenderHoursSourceEnum,
  /**
   * The budget actually being distributed, after that fallback: whichever of
   * laborHours / manualLaborHours hoursSource resolves to. Null under the same
   * conditions as laborHours (no average rate, or a non-RON project).
   */
  budgetLaborHours: z.number().nullable(),

  reservedHours: tenderReservedHours,
  /**
   * When the budget was last handed out across the operations by pressing
   * "Distribuie dupa procente" — null while that has never been done.
   *
   * Recorded rather than derived: whether the reserved hours happen to add up
   * to the budget is a different question from whether anyone has deliberately
   * distributed them, and the project list reports the latter. Hand-editing an
   * operation's hours afterwards therefore does not clear this.
   */
  hoursDistributedAt: z.string().datetime().nullable(),
  /** null until the tender is saved for the first time (defaults shown). */
  updatedAt: z.string().datetime().nullable(),
});
export type ProjectTenderResponse = z.infer<typeof projectTenderResponse>;

// .strict() is applied explicitly here (and on the nested objects above):
// the global validation pipe only strict-upgrades plain ZodObject bodies, and
// .refine() wraps this schema in ZodEffects, which would otherwise let typoed
// keys (e.g. `comissionPct`) be silently stripped instead of rejected.
export const updateProjectTenderRequest = z
  .object({
    profitPct: percentValue.optional(),
    commissionPct: percentValue.optional(),
    additionalPct: percentValue.optional(),
    /** Explicit null clears the manual scenario back to "not entered". */
    manualMaterialsCost: moneyValue.nullable().optional(),
    manualProfitPct: percentValue.optional(),
    manualCommissionPct: percentValue.optional(),
    manualAdditionalPct: percentValue.optional(),
    /** Which scenario's hours the operation distribution should work from. */
    hoursSource: tenderHoursSourceEnum.optional(),
    /** Only the operations being changed. */
    reservedHours: perOperation(reservedHoursValue).partial().strict().optional(),
    /**
     * Stamps `hoursDistributedAt`. Sent by the "distribute by shares" action
     * alongside the hours it writes, so the two land in one transaction and a
     * project can never be marked distributed without the hours to match.
     * `false` clears the stamp back to "never distributed".
     */
    markHoursDistributed: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.hoursSource !== undefined ||
      v.markHoursDistributed !== undefined ||
      v.profitPct !== undefined ||
      v.commissionPct !== undefined ||
      v.additionalPct !== undefined ||
      // `null` is a meaningful value here (clear), so only `undefined` counts
      // as "field absent" — hence the explicit undefined comparison.
      v.manualMaterialsCost !== undefined ||
      v.manualProfitPct !== undefined ||
      v.manualCommissionPct !== undefined ||
      v.manualAdditionalPct !== undefined ||
      (v.reservedHours !== undefined &&
        Object.values(v.reservedHours).some((value) => value !== undefined)),
    { message: 'At least one field is required.' },
  );
export type UpdateProjectTenderRequest = z.infer<typeof updateProjectTenderRequest>;

/**
 * One row of the project table the page opens on: everything the row shows,
 * already costed, so the list does not have to fetch a tender per project.
 *
 * The percentages travel without their money amounts — the row shows the
 * percent, and the figures those percentages produce are only meaningful next
 * to the rest of the costing, which is what selecting the project opens.
 */
export const projectTenderListItem = z.object({
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  currency: z.string(),
  totalOfferNet: decimalString,
  materialsCost: decimalString,
  /** As on the tender: says whether the figure came from the project's manual pricing. */
  materialsCostIsManual: z.boolean(),
  profitPct: z.number(),
  commissionPct: z.number(),
  additionalPct: z.number(),
  /** The automatic scenario's labour budget; negative when the offer does not cover the costs. */
  laborCost: decimalString,
  /** totalOfferNet / materialsCost; null when there are no materials to divide by. */
  coefficient: z.number().nullable(),
  /**
   * Whether the labour-hour budget has ever been distributed across the
   * operations for this project — the tick/cross in the last column.
   */
  hoursDistributed: z.boolean(),
});
export type ProjectTenderListItem = z.infer<typeof projectTenderListItem>;

export const listProjectTendersQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  // Capped well below the usual 100: every row costs a BOM roll-up, so an
  // unbounded page here would be an expensive request rather than a big one.
  pageSize: z.coerce.number().int().min(1).max(50).default(10),
  search: z.string().trim().max(100).optional(),
});
export type ListProjectTendersQuery = z.infer<typeof listProjectTendersQuery>;

export const listProjectTendersResponse = paginatedResponse(projectTenderListItem);
export type ListProjectTendersResponse = z.infer<typeof listProjectTendersResponse>;
